import { useState } from 'react';
import { CalendarDays, CalendarRange, ChevronDown, ChevronUp } from 'lucide-react';
import type { ReactNode } from 'react';
import type { EventAgendaPage, EventSummary } from '../api/events';
import { Button } from './Button';
import { EmptyState } from './EmptyState';

/**
 * What the events page opens on — TROOP-213.
 *
 * **One component, both doors.** `UnitHomeScreen` and `FamilyScreen` already share `MonthBar` and
 * `EventMonthSection`, and rule 13b is that `/family` mirrors `/units` in routes and never in
 * rules — so a lookalike here would be two answers to "what is on this page" that drift the first
 * time either is touched. Only the *row* differs between them, which is what `renderItem` is for.
 *
 * ## Why two groups and not one list
 *
 * The calendar takes months whole, deliberately (`EventMonthWindow`). That is right for browsing
 * and wrong for landing: on the 4th of the month the top of the page is the 1st, so a reader
 * scrolls past their own history to reach the thing they came for.
 *
 * But *upcoming only* would have quietly removed a path people use. Showing the first three days
 * of September on the 4th is also how somebody reaches the campout they got back from yesterday —
 * and the gear close-out, the packing retrospective, the photographs and the requirement
 * completions all hang off exactly that event, all in the same Sunday afternoon. So **Just
 * finished** is functional rather than nostalgic. It sits below upcoming and never above, and it
 * stays small and collapsed: letting it grow into the history view is the temptation, and the
 * history view is the calendar.
 *
 * ## What is deliberately not here
 *
 * **No grouping by kind.** This page answers a time question — *what is happening Saturday*, *what
 * is next* — and separating kinds means scanning every group to answer either. Worse, the digest's
 * `DigestPriority` ordering would put a campout six weeks out above tonight's meeting: correct for
 * a summary read once, actively wrong for a page consulted on the way out the door. The job hiding
 * inside "group by kind" is a **filter**, and it belongs on the all-events view (TROOP-33).
 *
 * **No count-based cap.** The bound is the server's time window. A cap takes the earliest N, which
 * for a unit with weekly meetings is N meetings and no campout — the exact bug TROOP-200 fixed for
 * the digest, and the reason its per-kind answer exists at all. A page has no attention budget to
 * ration; scrolling is free, so the crowding problem does not exist to solve.
 */
export function EventAgenda({
  agenda,
  renderItem,
  onViewAll,
  emptyAction,
}: {
  agenda: EventAgendaPage;
  renderItem: (event: EventSummary) => ReactNode;
  /** Through to the month-navigable calendar, unchanged. */
  onViewAll: () => void;
  /**
   * Offered on the empty state **only if this viewer can act on it** — a parent never sees "Add an
   * event". The way through to the calendar is offered either way, because a unit whose events are
   * all in the past has a history to reach and an empty page would read as broken.
   */
  emptyAction?: { label: string; onClick: () => void };
}) {
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [showFinished, setShowFinished] = useState(false);
  const [showAllFinished, setShowAllFinished] = useState(false);

  const drawn = showAllUpcoming ? agenda.upcoming : agenda.upcoming.slice(0, UPCOMING_DRAWN);
  const held = agenda.upcoming.length - drawn.length;

  const finishedDrawn = showAllFinished
    ? agenda.justFinished
    : agenda.justFinished.slice(0, FINISHED_DRAWN);
  const finishedHeld = agenda.justFinished.length - finishedDrawn.length;

  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-5)' }}>
      <section>
        <Heading
          label="Coming up"
          count={agenda.upcoming.length}
          note={`The next ${agenda.looksAheadDays} days`}
        />

        {agenda.upcoming.length === 0 ? (
          <EmptyState
            icon={CalendarDays}
            headline={`Nothing in the next ${agenda.looksAheadDays} days`}
            // Says what it looked at. "Nothing coming up" over a two-month window is honest; the
            // same sentence over everything would be a lie, and this page cannot see everything.
            explanation="Everything further out, and everything already past, is on the calendar."
            action={emptyAction}
          />
        ) : (
          <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
            {drawn.map((event) => renderItem(event))}
          </div>
        )}

        {/* What was held back, said out loud. TROOP-13 took silent truncation out of the calendar
            for good reasons, and any bound here would put it straight back -- so the count is on
            the control that reveals the rest, the trade EventMonthSection already makes. */}
        {held > 0 && (
          <Reveal onClick={() => setShowAllUpcoming(true)}>
            Show {held} more in the next {agenda.looksAheadDays} days
          </Reveal>
        )}
      </section>

      {/* Below upcoming, always. Above it is where this page started, and the whole complaint. */}
      {agenda.justFinished.length > 0 && (
        <section>
          {/* Collapsed by default: it is where somebody goes on purpose, having just got back,
              rather than what the page is about. Its own count on the control, so a parent who
              wants Sunday's campout can see there is something there without opening it. */}
          <button
            type="button"
            onClick={() => setShowFinished((open) => !open)}
            aria-expanded={showFinished}
            className="t-label inline-flex items-center text-left"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-muted)',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-1)',
              minHeight: 'var(--tap-min)',
              cursor: 'pointer',
            }}
          >
            {showFinished ? (
              <ChevronUp size={14} aria-hidden="true" />
            ) : (
              <ChevronDown size={14} aria-hidden="true" />
            )}
            Just finished
            <span className="tabular" style={{ fontWeight: 400 }}>
              &middot; {agenda.justFinished.length}
            </span>
          </button>

          {showFinished && (
            <>
              <p
                className="t-caption"
                style={{
                  color: 'var(--color-text-secondary)',
                  margin: '0 0 var(--space-3)',
                  maxWidth: '52ch',
                }}
              >
                The last {agenda.looksBackDays} days. Gear to check back in, photographs to add and
                anything still to record all live on the event itself.
              </p>

              <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
                {finishedDrawn.map((event) => renderItem(event))}
              </div>

              {/* Bounded like the group above it, and for the reason the dev database keeps
                  demonstrating: expanding this drew 303 rows on the dev troop, which is the
                  4,165-button `/family` page again in a new place. Collapsed by default is not a
                  bound -- somebody taps it. */}
              {finishedHeld > 0 && (
                <Reveal onClick={() => setShowAllFinished(true)}>
                  Show {finishedHeld} more from the last {agenda.looksBackDays} days
                </Reveal>
              )}
            </>
          )}
        </section>
      )}

      {/* Partly navigation and partly a correctness feature: this view is bounded at both ends by
          a window, so the way to everything outside it has to be on the page. */}
      <div>
        <Button variant="secondary" onClick={onViewAll}>
          <CalendarRange size={16} aria-hidden="true" />
          View the whole calendar
        </Button>
      </div>
    </div>
  );
}

function Heading({ label, count, note }: { label: string; count: number; note: string }) {
  return (
    <div style={{ margin: '0 0 var(--space-3)' }}>
      <h3 className="t-label" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
        {label}
        {count > 0 && (
          <span className="tabular" style={{ fontWeight: 400 }}>
            {' '}
            &middot; {count}
          </span>
        )}
      </h3>
      <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        {note}
      </p>
    </div>
  );
}

function Reveal({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="t-caption text-left"
      style={{
        background: 'none',
        border: 'none',
        color: 'var(--color-text-secondary)',
        padding: 'var(--space-2) var(--space-1)',
        minHeight: 'var(--tap-min)',
        textDecoration: 'underline',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/**
 * How many upcoming events are drawn before the rest go behind a tap.
 *
 * **Its own number, and pointedly not `EventMonthWindow.Target`.** That 25 is the browsable
 * calendar's month target and means something else; borrowing it would couple two rules that must
 * stay free to move apart.
 *
 * Chosen so a real unit is never bounded at all — a troop over two months is roughly eight weekly
 * meetings, two campouts, a service project and a couple of committee meetings. This exists for
 * the calendar that is genuinely enormous, which on the dev database is every calendar.
 */
const UPCOMING_DRAWN = 20;

/**
 * And how many of *Just finished*, which is smaller because the group is.
 *
 * A fortnight of a real troop is a meeting or two and whatever weekend just happened. This is the
 * bound for the calendar that is enormous, and it is needed even though the group is collapsed by
 * default: collapsed is not a bound, because somebody opens it.
 */
const FINISHED_DRAWN = 8;
